import { useEffect } from "react";
import type { RefObject } from "react";

type UseIntersectionObserverArgs = {
  targetRef: RefObject<Element>;
  onIntersect: (entry: IntersectionObserverEntry) => void;
  onLeave?: (entry: IntersectionObserverEntry) => void;
  enabled?: boolean;
  root?: Element | null | (() => Element | null);
  rootMargin?: string;
  threshold?: number | number[];
};

const useIntersectionObserver = ({
  targetRef,
  onIntersect,
  onLeave,
  enabled = true,
  root = null,
  rootMargin = "0px",
  threshold = 0,
}: UseIntersectionObserverArgs) => {
  useEffect(() => {
    if (!enabled) return;
    const target = targetRef.current;
    if (!target) return;
    const resolvedRoot = typeof root === "function" ? root() : root;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            onIntersect(entry);
          } else if (onLeave) {
            onLeave(entry);
          }
        });
      },
      { root: resolvedRoot, rootMargin, threshold }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [enabled, onIntersect, onLeave, root, rootMargin, targetRef, threshold]);
};

export default useIntersectionObserver;
